import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, LogOut, Pencil, Plus, Trash2, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { Logo } from "../components/Logo";
import { toast } from "sonner";

const EMPTY_FORM = {
  title: "", category: "Residencial", location: "Terra Roxa — PR", year: "",
  area_m2: "", image_url: "", gallery: "", description: "", visible: true,
};

const INPUT = "w-full border border-slate-900/15 bg-white px-3.5 py-2.5 text-sm outline-none transition-colors placeholder:text-slate-400 focus:border-slate-900";

const LoginForm = () => {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 px-5">
      <form onSubmit={submit} className="w-full max-w-sm border border-white/10 bg-white/[0.03] p-8">
        <Logo dark />
        <h1 className="font-display mt-8 text-xl font-bold uppercase tracking-tight text-white">Painel do escritório</h1>
        <p className="mt-1 text-sm text-slate-400">Acesso restrito à equipe ZARI.</p>
        <input
          data-testid="admin-login-email-input"
          type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="E-mail" className={`${INPUT} mt-8 !border-white/15 !bg-transparent !text-white placeholder:!text-slate-500`}
        />
        <input
          data-testid="admin-login-password-input"
          type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
          placeholder="Senha" className={`${INPUT} mt-3 !border-white/15 !bg-transparent !text-white placeholder:!text-slate-500`}
        />
        {error && <p data-testid="admin-login-error" className="mt-3 text-xs text-red-400">{error}</p>}
        <button
          data-testid="admin-login-submit-button"
          disabled={loading}
          className="mt-6 flex w-full items-center justify-center gap-2 border border-[#C05621] bg-[#C05621] py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#a84a1c] disabled:opacity-60"
        >
          {loading && <Loader2 size={14} className="animate-spin" />}
          Entrar
        </button>
        <Link to="/" className="mt-5 block text-center font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-white">
          Voltar ao site
        </Link>
      </form>
    </div>
  );
};

const ProjectForm = ({ initial, onSave, onClose }) => {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({
        ...form,
        area_m2: parseFloat(form.area_m2) || null,
        gallery: form.gallery.split("\n").map((s) => s.trim()).filter(Boolean),
      });
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/60 p-4 backdrop-blur-sm" data-testid="admin-project-form">
      <form onSubmit={submit} className="my-auto w-full max-w-2xl bg-white p-6 sm:p-8">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="font-display text-xl font-bold uppercase tracking-tight text-slate-900">
            {initial.id ? "Editar projeto" : "Novo projeto"}
          </h2>
          <button type="button" onClick={onClose} aria-label="Fechar" className="text-slate-400 hover:text-slate-900"><X size={18} /></button>
        </div>
        <div className="grid max-h-[65vh] gap-3 overflow-y-auto pr-1 sm:grid-cols-2">
          <input required placeholder="Título *" value={form.title} onChange={set("title")} className={`${INPUT} sm:col-span-2`} data-testid="admin-form-title" />
          <select value={form.category} onChange={set("category")} className={INPUT} data-testid="admin-form-category">
            <option>Residencial</option>
            <option>Comercial</option>
            <option>Industrial</option>
            <option>Reforma</option>
          </select>
          <input type="number" placeholder="Área construída (m²)" value={form.area_m2} onChange={set("area_m2")} className={INPUT} data-testid="admin-form-area" />
          <input placeholder="Local (ex.: Terra Roxa — PR)" value={form.location} onChange={set("location")} className={INPUT} data-testid="admin-form-location" />
          <input placeholder="Ano (ex.: 2025)" value={form.year} onChange={set("year")} className={INPUT} data-testid="admin-form-year" />
          <input required placeholder="URL da imagem principal *" value={form.image_url} onChange={set("image_url")} className={`${INPUT} sm:col-span-2`} data-testid="admin-form-image-url" />
          <textarea rows={2} placeholder="URLs de imagens adicionais (uma por linha)" value={form.gallery} onChange={set("gallery")} className={`${INPUT} sm:col-span-2`} />
          <textarea rows={3} placeholder="Descrição do projeto" value={form.description} onChange={set("description")} className={`${INPUT} sm:col-span-2`} data-testid="admin-form-description" />
          <label className="flex items-center gap-2 text-sm text-slate-600 sm:col-span-2">
            <input type="checkbox" checked={form.visible} onChange={set("visible")} className="h-4 w-4 accent-[#C05621]" data-testid="admin-form-visible" />
            Exibir no site
          </label>
        </div>
        <button data-testid="admin-save-project-btn" disabled={saving} className="mt-6 flex w-full items-center justify-center gap-2 border border-slate-900 bg-slate-900 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#C05621] hover:border-[#C05621] disabled:opacity-60">
          {saving && <Loader2 size={14} className="animate-spin" />}
          Salvar projeto
        </button>
      </form>
    </div>
  );
};

const Panel = () => {
  const { user, logout } = useAuth();
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => {
    api.adminProjects().then(setProjects).catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(load, []);

  const remove = async (p) => {
    if (!window.confirm(`Excluir "${p.title}"?`)) return;
    try {
      await api.deleteProject(p.id);
      toast.success("Projeto excluído");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="min-h-screen bg-[#FBFBFC]" data-testid="admin-panel">
      <header className="border-b border-slate-900/10 bg-white">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 sm:px-8">
          <Link to="/"><Logo /></Link>
          <div className="flex items-center gap-4">
            <span className="hidden font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500 sm:inline">{user?.email}</span>
            <button onClick={logout} data-testid="admin-logout-btn" aria-label="Sair" className="flex h-10 w-10 items-center justify-center border border-slate-900/15 text-slate-600 transition-colors hover:border-slate-900 hover:text-slate-900">
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-2xl font-extrabold uppercase tracking-tight text-slate-900">Nossos projetos</h1>
          <button
            data-testid="admin-add-project-btn"
            onClick={() => setEditing(EMPTY_FORM)}
            className="flex items-center gap-2 border border-slate-900 bg-slate-900 px-5 py-3 font-mono text-[11px] uppercase tracking-[0.2em] text-white transition-colors hover:border-[#C05621] hover:bg-[#C05621]"
          >
            <Plus size={14} /> Novo projeto
          </button>
        </div>

        <div className="mt-8 overflow-x-auto border border-slate-900/10 bg-white">
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-900/10 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                <th className="px-4 py-3">Projeto</th>
                <th className="px-4 py-3">Categoria</th>
                <th className="px-4 py-3">Local / Ano</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((p) => (
                <tr key={p.id} className="border-b border-slate-900/5 last:border-0">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <img src={p.image_url} alt="" className="h-10 w-14 object-cover" />
                      <span className="font-medium text-slate-900">{p.title}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{p.category}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{[p.location, p.year].filter(Boolean).join(" · ")}</td>
                  <td className="px-4 py-3">
                    <span className={`font-mono text-[10px] uppercase tracking-wider ${p.visible ? "text-emerald-600" : "text-slate-400"}`}>
                      {p.visible ? "Visível" : "Oculto"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <button
                        data-testid={`admin-edit-${p.id}`}
                        onClick={() => setEditing({ ...p, area_m2: p.area_m2 ?? "", gallery: (p.gallery || []).join("\n") })}
                        aria-label="Editar"
                        className="flex h-9 w-9 items-center justify-center border border-slate-900/15 text-slate-600 transition-colors hover:border-slate-900 hover:text-slate-900"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        data-testid={`admin-delete-${p.id}`}
                        onClick={() => remove(p)}
                        aria-label="Excluir"
                        className="flex h-9 w-9 items-center justify-center border border-slate-900/15 text-slate-600 transition-colors hover:border-red-600 hover:text-red-600"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {projects.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-slate-400">Nenhum projeto cadastrado.</td></tr>
              )}
            </tbody>
          </table>
        </div>

      </main>

      {editing && (
        <ProjectForm
          initial={editing}
          onClose={() => setEditing(null)}
          onSave={async (data) => {
            if (editing.id) await api.updateProject(editing.id, data);
            else await api.createProject(data);
            toast.success("Projeto salvo");
            load();
          }}
        />
      )}
    </div>
  );
};

export default function AdminPage() {
  const { user, ready } = useAuth();
  if (!ready) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950">
        <Loader2 size={28} className="animate-spin text-slate-500" />
      </div>
    );
  }
  return user ? <Panel /> : <LoginForm />;
}
